// src/pages/Profile.jsx
import React, { useMemo, useState, useEffect } from "react";
import Hero from "../../components/ui/Hero.jsx";
import Card from "../../components/ui/Card.jsx";
import {
  Save,
  X,
  Edit3,
  Building2,
  Upload,
  Trash2,
  MapPin,
  UserCircle2,
  Package2,
} from "lucide-react";
import { getLenderProfile, saveLenderProfile } from "../../api/lenderPortal/lenderProfile";
import { api } from "../../api/client.js";
import PhoneField from "../../components/ui/PhoneField.jsx";
import { arePhonesValid, isValidPhone, PHONE_ERROR_GENERIC, toE164 } from "../../utils/validatePhone.js";

/*************************
 * Profile Page — uses shared <Hero /> and <Card /> to match the app
 *************************/

/* ========== helpers ========== */
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const storedPhone = (v) => toE164(v) || String(v || "").trim();

/* Fallback logo (reads optional global from window to avoid no-undef) */
const PROFILE_DEFAULT_LOGO_DATAURL =
  (typeof window !== "undefined" && window.DEFAULT_LOGO_DATAURL) ||
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop stop-color="%23A7F3D0" offset="0"/><stop stop-color="%2338BDF8" offset="1"/></linearGradient></defs><rect rx="24" ry="24" width="128" height="128" fill="%23ffffff" stroke="%23e5e7eb" stroke-width="4"/><g transform="translate(32,28)"><rect width="8" height="60" rx="4" fill="%2338BDF8"/><rect x="20" y="10" width="8" height="50" rx="4" fill="%230EA5E9"/><rect x="40" y="22" width="8" height="38" rx="4" fill="%231993D1"/><circle cx="72" cy="44" r="18" fill="url(%23g)" stroke="%23FACC15" stroke-width="10"/></g></svg>';

/**
 * Logo URLs from Frappe are relative (e.g. /files/logo.png).
 * Use same-origin /api proxy so backend URL and API key stay server-side only.
 */
function normalizeLogoUrl(logoUrl) {
  if (!logoUrl || typeof logoUrl !== 'string') {
    return logoUrl;
  }

  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://') || logoUrl.startsWith('data:')) {
    return logoUrl;
  }

  let path = logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`;

  // Fix previously normalized broken values like "/api/files/..."
  if (path.startsWith('/api/files/')) {
    path = path.replace(/^\/api/, '');
  }
  if (path.startsWith('/api/private/files/')) {
    path = path.replace(/^\/api/, '');
  }

  // Frappe file URLs are served directly from /files (or /private/files), not /api/files.
  if (path.startsWith('/files/') || path.startsWith('/private/files/')) {
    return path;
  }

  // Keep /api prefix for non-file relative endpoints.
  return `/api${path}`;
}

const Label = ({ children, required }) => (
  <label className="block text-xs font-medium text-gray-700 mb-1">
    {children} {required && <span className="text-red-500">*</span>}
  </label>
);

const ErrorText = ({ msg, id }) =>
  msg ? (
    <div id={id} className="mt-1 text-xs text-rose-600">
      {msg}
    </div>
  ) : null;

const ReadOnlyBox = ({ value }) => (
  <div className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 text-sm text-gray-900 flex items-center whitespace-normal break-words">
    {value || "—"}
  </div>
);

// Skeleton loader components - only for text content
const SkeletonText = ({ width = "w-32", className = "" }) => (
  <div className={`h-4 bg-sky-100 rounded animate-pulse ${width} ${className}`} />
);

const SkeletonTextInline = ({ width = "w-24", className = "" }) => (
  <span className={`inline-block h-4 bg-sky-100 rounded animate-pulse ${width} ${className}`} />
);

const TextInput = ({
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  type = "text",
  error,
  disabled,
}) => (
  <>
    <input
      type={type === "tel" ? "text" : type}
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      inputMode={type === "tel" ? "numeric" : type === "email" ? "email" : "text"}
      pattern={type === "tel" ? "[0-9]*" : undefined}
      placeholder={placeholder}
      aria-invalid={!!error}
      aria-describedby={error ? `${name}-error` : undefined}
      disabled={disabled}
      className={`h-11 w-full rounded-lg border px-4 text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-200 ${error ? "border-rose-500" : "border-gray-300"
        } ${disabled ? "bg-gray-50 text-gray-900" : ""}`}
    />
    <ErrorText id={`${name}-error`} msg={error} />
  </>
);

/* Use app Card styling for contacts */
const ContactCard = ({
  title,
  subtitle,
  icon,
  colorClass,
  editing,
  fields,
  values,
  errors,
  onChange,
  onBlur,
}) => (
  <Card className="p-4">
    <div className="flex items-start gap-3 mb-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClass}`}>
        {icon}
      </div>
      <div>
        <div className="text-sm font-semibold text-gray-900">{title}</div>
        <div className="text-xs text-gray-500">{subtitle}</div>
      </div>
    </div>

    <div className="grid grid-cols-1 gap-3">
      <div>
        <Label required>Name</Label>
        {editing ? (
          <TextInput
            name={fields.name}
            value={values[fields.name]}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="Full name"
            error={errors[fields.name]}
          />
        ) : (
          <ReadOnlyBox value={values[fields.name]} />
        )}
      </div>

      <div>
        <Label>Email</Label>
        {editing ? (
          <TextInput
            name={fields.email}
            value={values[fields.email]}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="name@company.com"
            type="email"
            error={errors[fields.email]}
          />
        ) : (
          <ReadOnlyBox value={values[fields.email]} />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Phone</Label>
          {editing ? (
            <PhoneField
              value={values[fields.phone]}
              required={false}
              mobileOnly={false}
              onChange={(next) => onChange({ target: { name: fields.phone, value: next } })}
              onBlur={() => onBlur({ target: { name: fields.phone, value: values[fields.phone] } })}
            />
          ) : (
            <ReadOnlyBox value={values[fields.phone]} />
          )}
        </div>
      </div>
    </div>
  </Card>
);

/* ========== main component ========== */
export default function Profile({ lender, onLogoChange }) {
  const [isEditing, setIsEditing] = useState(false);

  // Backend / saving state
  const [loadingProfile, setLoadingProfile] = useState(true); // Start as true to show skeleton initially
  const [saving, setSaving] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [backendError, setBackendError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  // State for docname (user email)
  const [docname, setDocname] = React.useState(() => {
    // Try to get user email from various sources
    if (typeof window !== "undefined") {
      return (
        lender?.email ||
        lender?.companyEmail ||
        sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        sessionStorage.getItem("DS_USER_EMAIL") ||
        localStorage.getItem("LENDER_EMAIL") ||
        ""
      );
    }
    return lender?.email || lender?.companyEmail || "";
  });

  // Logo state (preview + file to upload)
  const [logoUrl, setLogoUrl] = useState(() => {
    try {
      const stored = lender?.logoUrl || localStorage.getItem("lenderLogo") || "";
      // Normalize stored logo URL if it exists
      return stored ? normalizeLogoUrl(stored) : "";
    } catch {
      const stored = lender?.logoUrl || "";
      return stored ? normalizeLogoUrl(stored) : "";
    }
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoError, setLogoError] = useState("");

  // 🔹 NO DUMMY DATA: start with empty strings
  const [p, setP] = useState({
    companyName: "",
    companyEmail: "",
    companyPhone: "",

    officeName: "",
    officeEmail: "",
    officePhone: "",

    fieldName: "",
    fieldEmail: "",
    fieldPhone: "",

    pmName: "",
    pmEmail: "",
    pmPhone: "",
  });

  const [errors, setErrors] = useState({});
  const isPhoneKey = (k) => /phone/i.test(k);

  /* ---------- Fetch current user email from backend if not available ---------- */
  useEffect(() => {
    let cancelled = false;

    async function fetchCurrentUser() {
      // If we already have a docname, skip
      if (docname && docname.trim() !== "") {
        return;
      }

      try {
        console.log("🔍 Fetching current user from backend...");
        // Fetch current logged-in user from backend
        const response = await api.get("/api/method/frappe.auth.get_logged_user");
        console.log("👤 get_logged_user response:", response);
        
        // Frappe returns user data in different formats:
        // Option 1: { data: { message: { email: "...", ... } } }
        // Option 2: { data: { message: "user@email.com" } }
        // Option 3: { data: { email: "...", ... } }
        const userData = response?.data?.message || response?.data;
        let userEmail = null;
        
        if (typeof userData === 'string') {
          // User is returned as a string (email)
          userEmail = userData;
        } else if (userData && typeof userData === 'object') {
          // User is returned as an object
          userEmail = userData.email || userData.name || userData.user;
        }
        
        if (!cancelled && userEmail && typeof userEmail === 'string' && userEmail.trim() !== "") {
          console.log("✅ Current user email from backend:", userEmail);
          const trimmedEmail = userEmail.trim();
          setDocname(trimmedEmail);
          // Pre-fill company email with the user email
          setP(prev => ({
            ...prev,
            companyEmail: trimmedEmail
          }));
          // Also store it for future use
          try {
            sessionStorage.setItem("user", trimmedEmail);
            localStorage.setItem("user", trimmedEmail);
          } catch (e) {
            console.warn("Failed to store user email:", e);
          }
        } else {
          console.warn("⚠️ Could not extract user email from response:", userData);
          // Still allow form to be shown
          if (!cancelled) {
            setLoadingProfile(false);
          }
        }
      } catch (error) {
        console.error("❌ Failed to fetch current user:", error);
        console.error("Error details:", error.response?.data || error.message);
        // Set an error message but still allow form to be shown
        setBackendError("Unable to fetch user information. Please refresh the page or sign in again.");
        // Set loading to false so the form can be displayed (even if empty)
        if (!cancelled) {
          setLoadingProfile(false);
        }
      }
    }

    fetchCurrentUser();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - don't depend on docname to avoid infinite loops

  /* ---------- Load existing profile from backend ---------- */
  useEffect(() => {
    let cancelled = false;

    async function loadProfile() {
      // Skip if no docname
      if (!docname || docname.trim() === "") {
        console.warn("⚠️ No docname available, cannot load profile");
        setLoadingProfile(false);
        return;
      }

      console.log("📋 Loading profile for docname:", docname);

      try {
        setLoadingProfile(true);
        setBackendError("");
        setSaveMessage("");

        const data = await getLenderProfile(docname);
        console.log("👤 Profile data from backend:", data);
        console.log("🔍 Field BDM fields in response:", {
          field_bdm_name: data.field_bdm_name,
          field_name: data.field_name,
          field_bdm_phone: data.field_bdm_phone,
          field_phone: data.field_phone,
          field_phone_number: data.field_phone_number,
          field_phone_num: data.field_phone_num,
          field_bdm_email: data.field_bdm_email,
          field_email: data.field_email,
          field_bdm_landline: data.field_bdm_landline,
          field_landline: data.field_landline,
        });
        console.log("🔍 All field-related keys in response:", Object.keys(data).filter(k => k.toLowerCase().includes('field')));
        if (cancelled || !data) return;

        setHasProfile(true);

        // 🔹 OVERWRITE WITH PURE BACKEND DATA (matching actual backend field names)
        setP({
          companyName: data.company_name || "",
          // Email comes from sign-in (docname), use it as fallback
          companyEmail: data.company_email || data.email_address || docname || "",
          companyPhone: data.company_phone || data.phone_number
            ? storedPhone(data.company_phone || data.phone_number)
            : "",

          // Backend uses office_* instead of office_bdm_*
          officeName: data.office_bdm_name || data.office_name || "",
          officeEmail: data.office_bdm_email || data.office_email || "",
          officePhone: data.office_bdm_phone || data.office_phone
            ? storedPhone(data.office_bdm_phone || data.office_phone)
            : "",

          // Check multiple possible field names for Field BDM
          // Backend returns: field_name, field_email, field_landline (but NOT field_phone!)
          fieldName: data.field_bdm_name || data.field_name || data.field_bdm || "",
          fieldEmail: data.field_bdm_email || data.field_email || "",
          // Backend doesn't seem to return field_phone - check all possible variations
          fieldPhone: data.field_bdm_phone || data.field_phone || data.field_bdm_phone_number || data.field_phone_number || data.field_phone_num
            ? storedPhone(data.field_bdm_phone || data.field_phone || data.field_bdm_phone_number || data.field_phone_number || data.field_phone_num)
            : "",

          pmName: data.product_manager_name || "",
          pmEmail: data.product_manager_email || "",
          pmPhone: data.product_manager_phone
            ? storedPhone(data.product_manager_phone)
            : "",
        });

        const backendLogo =
          data.lender_logo || data.logo || data.logo_url || "";
        if (backendLogo) {
          const normalizedLogoUrl = normalizeLogoUrl(backendLogo);
          setLogoUrl(normalizedLogoUrl);
          try {
            localStorage.setItem("lenderLogo", normalizedLogoUrl);
          } catch {
            /* ignore */
          }
        }
      } catch (err) {
        const msg = String(err?.message || "");
        const status = err?.status || err?.response?.status;
        
        // 404 = no profile yet, that's OK - show empty form
        if (status === 404 || msg.includes("404") || msg.toLowerCase().includes("not found")) {
          setHasProfile(false);
          console.log("ℹ️ No profile found yet (404) - this is normal for new lenders, showing empty form");
          // Don't set error - empty form is expected for new profiles
          // Pre-fill company email with docname (user email from login)
          if (docname) {
            setP(prev => ({
              ...prev,
              companyEmail: docname
            }));
          }
        } else {
          console.error("❌ Failed to load lender profile:", err);
          setBackendError(`Failed to load profile: ${msg || "Unknown error"}`);
          // Even on error, allow user to fill in the form
          // Pre-fill company email with docname if available
          if (docname) {
            setP(prev => ({
              ...prev,
              companyEmail: docname
            }));
          }
        }
      } finally {
        if (!cancelled) {
          setLoadingProfile(false);
        }
      }
    }

    // Only try to load profile if we have a docname
    if (docname && docname.trim() !== "") {
      loadProfile();
    } else {
      // No docname yet - fetchCurrentUser will set it, then this effect will re-run
      // Keep loading state until we have a docname or fetchCurrentUser fails
      // setLoadingProfile will be set to false in fetchCurrentUser's catch block if needed
    }

    return () => {
      cancelled = true;
    };
  }, [docname]); // Re-run when docname is set

  /* ---------- validation helpers ---------- */
  const validateField = (name, value) => {
    const v = String(value || "").trim();
    const req = new Set([
      "companyName",
      "companyEmail",
      "companyPhone",
      "officeName",
      "fieldName",
      "pmName",
    ]);
    if (req.has(name) && !v) return "This field is required.";
    if (/email/i.test(name)) {
      if (!v) return null;
      if (!emailRegex.test(v)) return "Enter a valid email address.";
    }
    if (isPhoneKey(name)) {
      const required = name === "companyPhone";
      if (!isValidPhone(v, { mobileOnly: false, required })) {
        return required || v ? PHONE_ERROR_GENERIC : null;
      }
    }
    return null;
  };

  const validateAll = (obj) => {
    const next = {};
    Object.entries(obj).forEach(([k, v]) => {
      const e = validateField(k, v);
      if (e) next[k] = e;
    });
    return next;
  };

  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (isPhoneKey(name)) {
      setP((s) => ({ ...s, [name]: value }));
      const eMsg = validateField(name, value);
      setErrors((prev) => {
        const n = { ...prev };
        if (eMsg) n[name] = eMsg;
        else delete n[name];
        return n;
      });
      return;
    }
    setP((s) => ({ ...s, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const eMsg = validateField(name, value);
    setErrors((prev) => {
      const n = { ...prev };
      if (eMsg) n[name] = eMsg;
      else delete n[name];
      return n;
    });
  };

  /* ---------- Logo upload handlers ---------- */
  const onSelectLogo = (file) => {
    if (!file) return;
    setLogoError("");

    const validType = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"].includes(
      file.type
    );
    const validSize = file.size <= 1024 * 1024 * 2;
    if (!validType || !validSize) {
      setLogoError("Please upload a PNG/JPG/SVG up to 2MB.");
      return;
    }

    setLogoFile(file);

    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result || "");
      setLogoUrl(url);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoUrl("");
    setLogoFile(null);
    setLogoError("");
  };

  const onEdit = () => {
    setErrors(validateAll(p));
    setIsEditing(true);
    setSaveMessage("");
    setBackendError("");
  };

  const onSave = async () => {
    if (!docname || docname.trim() === "") {
      setBackendError("Unable to save: No lender email found. Please sign in.");
      return;
    }

    const n = validateAll(p);
    setErrors(n);
    if (Object.keys(n).length) return;

    setBackendError("");
    setSaveMessage("");
    setSaving(true);
    try {
      // Map UI state to Frappe Lender Profile fields (using backend's actual field names)
      const payload = {
        // Backend Lender Profile requires the `user` field for naming/permissions.
        // Use docname (email) as the user value, same as Borrower Profile.
        user: docname || "",
        company_name: p.companyName,
        // Email comes from sign-in, don't send it in payload (backend uses email_address from user)
        // company_email: p.companyEmail, // Removed - email comes from sign-in
        // email_address: p.companyEmail, // Removed - email comes from sign-in
        company_phone: toE164(p.companyPhone) || p.companyPhone,
        phone_number: toE164(p.companyPhone) || p.companyPhone,

        // Backend uses office_* (not office_bdm_*)
        office_bdm_name: p.officeName,
        office_name: p.officeName, // Also send as office_name
        office_bdm_email: p.officeEmail,
        office_email: p.officeEmail, // Also send as office_email
        office_bdm_phone: toE164(p.officePhone) || p.officePhone,
        office_phone: toE164(p.officePhone) || p.officePhone,

        // Send both field_bdm_* and field_* to cover all cases
        // Backend might use field_phone_number (like phone_number for company)
        field_bdm_name: p.fieldName,
        field_name: p.fieldName, // Also send as field_name
        field_bdm_email: p.fieldEmail,
        field_email: p.fieldEmail, // Also send as field_email
        field_bdm_phone: toE164(p.fieldPhone) || p.fieldPhone,
        field_phone: toE164(p.fieldPhone) || p.fieldPhone,
        field_phone_number: toE164(p.fieldPhone) || p.fieldPhone,

        product_manager_name: p.pmName,
        product_manager_email: p.pmEmail,
        product_manager_phone: toE164(p.pmPhone) || p.pmPhone,
      };

      const saved = await saveLenderProfile(docname, payload, {
        logoFile,
        exists: hasProfile,
      });

      setHasProfile(true);

      // Reload profile from backend to ensure state matches saved data
      try {
        const reloaded = await getLenderProfile(docname);
        if (reloaded) {
          setP({
            companyName: reloaded.company_name || "",
            // Email comes from sign-in (docname), use it as fallback
            companyEmail: reloaded.company_email || reloaded.email_address || docname || "",
            companyPhone: reloaded.company_phone || reloaded.phone_number
              ? storedPhone(reloaded.company_phone || reloaded.phone_number)
              : "",

            // Backend uses office_* instead of office_bdm_*
            officeName: reloaded.office_bdm_name || reloaded.office_name || "",
            officeEmail: reloaded.office_bdm_email || reloaded.office_email || "",
            officePhone: reloaded.office_bdm_phone || reloaded.office_phone
              ? storedPhone(reloaded.office_bdm_phone || reloaded.office_phone)
              : "",

            // Check multiple possible field names for Field BDM
            // Backend returns: field_name, field_email, field_landline (but NOT field_phone!)
            fieldName: reloaded.field_bdm_name || reloaded.field_name || reloaded.field_bdm || "",
            fieldEmail: reloaded.field_bdm_email || reloaded.field_email || "",
            // Backend doesn't seem to return field_phone - check all possible variations
            fieldPhone: reloaded.field_bdm_phone || reloaded.field_phone || reloaded.field_bdm_phone_number || reloaded.field_phone_number || reloaded.field_phone_num
              ? storedPhone(reloaded.field_bdm_phone || reloaded.field_phone || reloaded.field_bdm_phone_number || reloaded.field_phone_number || reloaded.field_phone_num)
              : "",

            pmName: reloaded.product_manager_name || "",
            pmEmail: reloaded.product_manager_email || "",
            pmPhone: reloaded.product_manager_phone
              ? storedPhone(reloaded.product_manager_phone)
              : "",
          });
        }
      } catch (reloadErr) {
        console.warn("Failed to reload profile after save:", reloadErr);
        // Continue anyway - the save succeeded
      }

      const backendLogo =
        saved?.lender_logo || saved?.logo || saved?.logo_url || "";
      if (backendLogo) {
        const normalizedLogoUrl = normalizeLogoUrl(backendLogo);
        setLogoUrl(normalizedLogoUrl);
        try {
          localStorage.setItem("lenderLogo", normalizedLogoUrl);
        } catch {
          /* ignore */
        }
      } else if (logoUrl) {
        // fallback: still cache preview (already normalized if from backend)
        try {
          localStorage.setItem("lenderLogo", logoUrl);
        } catch {
          /* ignore */
        }
      }

      if (typeof onLogoChange === "function") {
        onLogoChange(backendLogo || logoUrl || "");
      }

      setLogoFile(null);
      setIsEditing(false);
      setSaveMessage("Profile saved successfully.");
    } catch (err) {
      console.error("❌ Save profile error:", err);
      const errorMsg = err?.response?.data?.message || err?.message || "Unknown error";
      setBackendError(`Failed to save profile: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const onCancel = async () => {
    // Reload profile from backend to discard unsaved changes
    if (docname && docname.trim() !== "") {
      try {
        const data = await getLenderProfile(docname);
        if (data) {
          setP({
            companyName: data.company_name || "",
            // Email comes from sign-in (docname), use it as fallback
            companyEmail: data.company_email || data.email_address || docname || "",
            companyPhone: data.company_phone || data.phone_number
              ? storedPhone(data.company_phone || data.phone_number)
              : "",

            // Backend uses office_* instead of office_bdm_*
            officeName: data.office_bdm_name || data.office_name || "",
            officeEmail: data.office_bdm_email || data.office_email || "",
            officePhone: data.office_bdm_phone || data.office_phone
              ? storedPhone(data.office_bdm_phone || data.office_phone)
              : "",

            // Check multiple possible field names for Field BDM
            // Backend returns: field_name, field_email, field_landline (but NOT field_phone!)
            fieldName: data.field_bdm_name || data.field_name || data.field_bdm || "",
            fieldEmail: data.field_bdm_email || data.field_email || "",
            // Backend doesn't seem to return field_phone - check all possible variations
            fieldPhone: data.field_bdm_phone || data.field_phone || data.field_bdm_phone_number || data.field_phone_number || data.field_phone_num
              ? storedPhone(data.field_bdm_phone || data.field_phone || data.field_bdm_phone_number || data.field_phone_number || data.field_phone_num)
              : "",

            pmName: data.product_manager_name || "",
            pmEmail: data.product_manager_email || "",
            pmPhone: data.product_manager_phone
              ? storedPhone(data.product_manager_phone)
              : "",
          });

          const backendLogo =
            data.lender_logo || data.logo || data.logo_url || "";
          if (backendLogo) {
            const normalizedLogoUrl = normalizeLogoUrl(backendLogo);
            setLogoUrl(normalizedLogoUrl);
          }
        }
      } catch (err) {
        console.warn("Failed to reload profile on cancel:", err);
      }
    }
    setLogoFile(null);
    setIsEditing(false);
    setErrors({});
    setBackendError("");
    setSaveMessage("");
  };

  return (
    <>
      <Hero
        title="Profile"
        subtitle="Configure profile, key contacts, and details."
      />

      <div className="grid gap-4 sm:gap-6 mt-4">
        {/* Basic Information */}
        <Card className="p-4 sm:p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-sky-500" />
              <h2 className="text-xl font-semibold">Basic Information</h2>
            </div>
            <div className="flex gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={onSave}
                    disabled={
                      hasErrors ||
                      saving ||
                      !isValidPhone(p.companyPhone, { mobileOnly: false }) ||
                      !arePhonesValid([
                        { value: p.officePhone, mobileOnly: false, required: false },
                        { value: p.fieldPhone, mobileOnly: false, required: false },
                        { value: p.pmPhone, mobileOnly: false, required: false },
                      ])
                    }
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 transition ${hasErrors || saving || !isValidPhone(p.companyPhone, { mobileOnly: false })
                      ? "bg-white/70 text-sky-700 cursor-not-allowed"
                      : "bg-sky-500 text-white hover:bg-sky-600"
                      }`}
                  >
                    <Save size={18} />
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    onClick={onCancel}
                    className="inline-flex items-center gap-2 rounded-xl border border-sky-300 px-4 py-2.5 text-sky-700 hover:bg-sky-50 transition"
                  >
                    <X size={18} /> Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={onEdit}
                  className="inline-flex items-center gap-2 rounded-xl border border-sky-300 px-4 py-2.5 text-sky-700 hover:bg-sky-50 transition"
                >
                  <Edit3 size={18} /> Edit Profile
                </button>
              )}
            </div>
          </div>

          {(backendError || saveMessage) && (
            <div className="mb-3 text-xs">
              {backendError && (
                <span className="text-rose-600">{backendError}</span>
              )}
              {saveMessage && (
                <span className="text-emerald-600 ml-2">{saveMessage}</span>
              )}
            </div>
          )}

          {!docname && !loadingProfile && (
            <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-4">
              <p className="text-sm text-amber-800">
                ⚠️ Unable to determine user email. Please refresh the page or sign in again.
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Debug: docname={docname || "(empty)"}, loading={String(loadingProfile)}
              </p>
            </div>
          )}

          {loadingProfile ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label required>Company Name</Label>
                <div className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 flex items-center">
                  <SkeletonText width="w-40" />
                </div>
              </div>
              <div>
                <Label required>Email Address</Label>
                <div className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 flex items-center">
                  <SkeletonText width="w-48" />
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  <SkeletonTextInline width="w-36" />
                </div>
              </div>
              <div>
                <Label required>Phone Number</Label>
                <div className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 flex items-center">
                  <SkeletonText width="w-32" />
                </div>
              </div>
              <div>
                <Label>Lender Logo</Label>
                <div className="flex items-start gap-4">
                  <div className="h-20 w-36 rounded-xl bg-white border border-gray-200 overflow-hidden flex items-center justify-center shrink-0">
                    <div className="h-full w-full bg-sky-100 animate-pulse" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mt-2">
                      <SkeletonTextInline width="w-48" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label required>Company Name</Label>
                {isEditing ? (
                  <TextInput
                    name="companyName"
                    value={p.companyName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Company Name"
                    error={errors.companyName}
                  />
                ) : (
                  <ReadOnlyBox value={p.companyName} />
                )}
              </div>

              <div>
                <Label required>Email Address</Label>
                <ReadOnlyBox value={p.companyEmail || docname || "Not available"} />
                <div className="mt-1 text-xs text-gray-500">
                  {docname ? "Email is set from your sign-in and cannot be changed" : "Email will be set from your sign-in"}
                </div>
              </div>

              <div>
                <Label required>Phone Number</Label>
                {isEditing ? (
                  <PhoneField
                    value={p.companyPhone}
                    mobileOnly={false}
                    onChange={(next) => handleChange({ target: { name: "companyPhone", value: next } })}
                    onBlur={() => handleBlur({ target: { name: "companyPhone", value: p.companyPhone } })}
                  />
                ) : (
                  <ReadOnlyBox value={p.companyPhone} />
                )}
              </div>

              {/* Logo upload + preview */}
              <div>
                <Label>Lender Logo</Label>
                <div className="flex items-start gap-4">
                  <div className="h-20 w-36 rounded-xl bg-white border border-gray-200 overflow-hidden flex items-center justify-center shrink-0">
                    <img
                      src={logoUrl || PROFILE_DEFAULT_LOGO_DATAURL}
                      alt="Lender logo"
                      className="h-full w-full object-contain p-2"
                      onError={(e) => {
                        const current = String(e.currentTarget.src || "");
                        // If old bad "/api/files/..." URL is still present somewhere, retry once with "/files/...".
                        if (current.includes("/api/files/")) {
                          e.currentTarget.src = current.replace("/api/files/", "/files/");
                          return;
                        }
                        e.currentTarget.src = PROFILE_DEFAULT_LOGO_DATAURL;
                      }}
                    />
                  </div>

                  {isEditing ? (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                      <label className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
                        <Upload className="h-4 w-4" />
                        <span>Upload image</span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                          className="hidden"
                          onChange={(e) => onSelectLogo(e.target.files?.[0])}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </button>
                      <div className="text-xs text-gray-500">
                        PNG/JPG/SVG, up to 2MB. Any logo shape is supported (wide/tall/square).
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 mt-2">
                      Update your brand logo via <strong>Edit Profile</strong>.
                    </div>
                  )}
                </div>
                {logoError && (
                  <div className="mt-2 text-xs text-rose-600">{logoError}</div>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Key Contacts */}
        <div>
          <div className="mb-2 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-sky-500" />
            <h2 className="text-xl font-semibold">Key Contacts</h2>
          </div>

          {loadingProfile ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Office BDM Skeleton */}
              <Card className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <UserCircle2 className="w-5 h-5 text-blue-700" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 mb-0.5">
                      <SkeletonTextInline width="w-24" />
                    </div>
                    <div className="text-xs text-gray-500">
                      <SkeletonTextInline width="w-40" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label required>Name</Label>
                    <div className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 flex items-center">
                      <SkeletonText width="w-32" />
                    </div>
                  </div>
                  <div>
                    <Label required>Email</Label>
                    <div className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 flex items-center">
                      <SkeletonText width="w-40" />
                    </div>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <div className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 flex items-center">
                      <SkeletonText width="w-28" />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Field BDM Skeleton */}
              <Card className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-green-700" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 mb-0.5">
                      <SkeletonTextInline width="w-24" />
                    </div>
                    <div className="text-xs text-gray-500">
                      <SkeletonTextInline width="w-36" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label required>Name</Label>
                    <div className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 flex items-center">
                      <SkeletonText width="w-32" />
                    </div>
                  </div>
                  <div>
                    <Label required>Email</Label>
                    <div className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 flex items-center">
                      <SkeletonText width="w-40" />
                    </div>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <div className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 flex items-center">
                      <SkeletonText width="w-28" />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Product Manager Skeleton */}
              <Card className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                    <Package2 className="w-5 h-5 text-purple-700" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 mb-0.5">
                      <SkeletonTextInline width="w-32" />
                    </div>
                    <div className="text-xs text-gray-500">
                      <SkeletonTextInline width="w-44" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label required>Name</Label>
                    <div className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 flex items-center">
                      <SkeletonText width="w-32" />
                    </div>
                  </div>
                  <div>
                    <Label required>Email</Label>
                    <div className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 flex items-center">
                      <SkeletonText width="w-40" />
                    </div>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <div className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 flex items-center">
                      <SkeletonText width="w-28" />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ContactCard
                title="Office BDM"
                subtitle="Business Development Manager"
                colorClass="bg-blue-50 text-blue-700"
                icon={<UserCircle2 className="w-5 h-5" />}
                editing={isEditing}
                fields={{
                  name: "officeName",
                  email: "officeEmail",
                  phone: "officePhone",
                }}
                values={p}
                errors={errors}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              <ContactCard
                title="Field BDM"
                subtitle="Field Business Development"
                colorClass="bg-green-50 text-green-700"
                icon={<MapPin className="w-5 h-5" />}
                editing={isEditing}
                fields={{
                  name: "fieldName",
                  email: "fieldEmail",
                  phone: "fieldPhone",
                }}
                values={p}
                errors={errors}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              <ContactCard
                title="Product Manager"
                subtitle="Lending Products & Services"
                colorClass="bg-purple-50 text-purple-700"
                icon={<Package2 className="w-5 h-5" />}
                editing={isEditing}
                fields={{
                  name: "pmName",
                  email: "pmEmail",
                  phone: "pmPhone",
                }}
                values={p}
                errors={errors}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}


